import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import type { ProductType, ProductVariantSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Input } from '@/components/ui/Input';
import { apiErrorDetails, apiErrorMessage } from '@/services/apiClient';
import { useAddVariantMutation, useUpdateVariantMutation } from '../api/catalog.api';
import {
  buildVariantPayload,
  emptyVariantFormFor,
  EMPTY_VARIANT_FORM,
  hasVariantErrors,
  supplyModeRefusal,
  toVariantFormValues,
  validateVariantForm,
  type VariantFormErrors,
  type VariantFormValues,
} from '../variant-form';
import { VariantDetailFields } from './VariantDetailFields';

interface VariantFormDrawerProps {
  productId: string;
  productType: ProductType;
  /** Absent when adding. */
  variant?: ProductVariantSummary;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}

/** Add or edit a variant, including the optical fields its product type calls for. */
export function VariantFormDrawer({
  productId,
  productType,
  variant,
  isOpen,
  onClose,
  onSaved,
}: VariantFormDrawerProps) {
  const { t } = useTranslation();
  const { currencyCode } = useCurrency();
  const isEditing = Boolean(variant);

  const [values, setValues] = useState<VariantFormValues>(EMPTY_VARIANT_FORM);
  const [errors, setErrors] = useState<VariantFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const addVariant = useAddVariantMutation(productId);
  const updateVariant = useUpdateVariantMutation(productId, variant?.id ?? '');
  const mutation = isEditing ? updateVariant : addVariant;

  // Re-seed on open so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!isOpen) return;

    setValues(
      variant
        ? toVariantFormValues(variant, productType, currencyCode)
        : emptyVariantFormFor(productType),
    );
    setErrors({});
    setSubmitError(null);
  }, [isOpen, productType, variant, currencyCode]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    const validation = validateVariantForm(values, productType, t, currencyCode);
    setErrors(validation);
    if (hasVariantErrors(validation)) return;

    const payload = buildVariantPayload(values, productType, currencyCode);
    // The barcode is only meaningful when creating; existing variants manage theirs separately.
    if (isEditing) delete payload.barcode;

    mutation.mutate(payload, {
      onSuccess: () => {
        onSaved(isEditing ? t('catalog.variants.updated') : t('catalog.variants.added'));
        onClose();
      },
      onError: (caught) => {
        // The server decides whether a lens may change supply mode; say why in the user's language.
        const refusal = supplyModeRefusal(apiErrorDetails(caught));
        setSubmitError(
          refusal?.kind === 'stockHistory'
            ? t('catalog.supplyModeRefused.stockHistory')
            : refusal?.kind === 'openOrder'
              ? t('catalog.supplyModeRefused.openOrder', { reference: refusal.reference })
              : (apiErrorMessage(caught) ?? t('catalog.variants.saveError')),
        );
      },
    });
  };

  const setDetail = (key: string, value: string) => {
    setValues((current) => ({ ...current, details: { ...current.details, [key]: value } }));
    setErrors((current) => ({
      ...current,
      details: { ...(current.details ?? {}), [key]: '' },
    }));
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('catalog.variants.editTitle') : t('catalog.variants.addTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="variant-form" isLoading={mutation.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form id="variant-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('catalog.fields.sku')}
            dir="ltr"
            hint={isEditing ? undefined : t('catalog.fields.skuHint')}
            value={values.sku}
            error={errors.sku}
            onChange={(event) => setValues((c) => ({ ...c, sku: event.target.value }))}
          />

          <Input
            label={t('catalog.fields.variantName')}
            value={values.variantName}
            hint={t('catalog.fields.variantNameHint')}
            onChange={(event) => setValues((c) => ({ ...c, variantName: event.target.value }))}
          />

          <Input
            label={t('catalog.fields.costPrice')}
            type="number"
            step="0.01"
            min={0}
            dir="ltr"
            value={values.costPrice}
            error={errors.costPrice}
            onChange={(event) => setValues((c) => ({ ...c, costPrice: event.target.value }))}
          />

          <Input
            label={t('catalog.fields.sellingPrice')}
            required
            type="number"
            step="0.01"
            min={0}
            dir="ltr"
            value={values.sellingPrice}
            error={errors.sellingPrice}
            onChange={(event) => setValues((c) => ({ ...c, sellingPrice: event.target.value }))}
          />
        </div>

        {!isEditing ? (
          <Input
            label={t('catalog.fields.barcode')}
            dir="ltr"
            hint={t('catalog.fields.barcodeHint')}
            value={values.barcode}
            error={errors.barcode}
            onChange={(event) => setValues((c) => ({ ...c, barcode: event.target.value }))}
          />
        ) : null}

        <div className="border-line border-t pt-4">
          <h3 className="text-ink mb-3 text-sm font-semibold">
            {t(`catalog.types.${productType}`)} · {t('catalog.variants.specifications')}
          </h3>

          <VariantDetailFields
            type={productType}
            values={values.details}
            errors={errors.details ?? {}}
            onChange={setDetail}
          />
        </div>
      </form>
    </Drawer>
  );
}
